import PropTypes from 'prop-types';
import React, { useCallback, useContext, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Stack } from '@fluentui/react/lib/Stack';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Card,
  Subtitle1,
  makeStyles,
} from '@fluentui/react-components';
import { OverviewTable } from './overview-table';
import { PositivePerformingAudiencesCard } from './positive-performing-audiences-card';

const useStyles = makeStyles({
  accordionHeader: {
    fontWeight: '600',
  },
  card: {
    paddingBottom: '24px',
  },
  heading: {
    marginTop: '36px !important',
  },
});

const layoutTokens = {
  childrenGap: 15,
};

export const AudienceOverviewSection = ({
  conversionBottomPerformerData,
  conversionData,
  conversionTopPerformerData,
  measurementTypes,
  searchBottomPerformerData,
  searchData,
  searchTopPerformerData,
  visitBottomPerformerData,
  visitData,
  visitTopPerformerData,
}) => {
  const [openItems, setOpenItems] = useState([1]);
  const onToggle = useCallback((event, data) => {
    setOpenItems(data.openItems);
  }, []);

  const styles = useStyles();

  const i18n = useContext(I18nContext);
  const sectionTitle = i18n.getString(_TL_('Audience overview'));
  const searchLiftTitle = i18n.getString(_TL_('Search lift'));
  const visitLiftTitle = i18n.getString(_TL_('Visit lift'));
  const conversionLiftTitle = i18n.getString(_TL_('Conversion lift'));
  const audienceTypeColumnHeader = i18n.getString(_TL_('Audience type'));
  const { useConversionLift, useSearchLift, useVisitLift } = measurementTypes;

  return (
    <>
      <Subtitle1 className={styles.heading}>{sectionTitle}</Subtitle1>
      <Accordion
        collapsible
        multiple
        onToggle={onToggle}
        openItems={openItems}
      >
        {useSearchLift && (
          <AccordionItem value={1} className="search-lift">
            <AccordionHeader as="h5" size="large">
              <span className={styles.accordionHeader}>
                {searchLiftTitle}
              </span>
            </AccordionHeader>
            <AccordionPanel>
              <Stack tokens={layoutTokens}>
                <Card className={styles.card}>
                  <OverviewTable
                    items={searchData}
                    liftHelpTopic="pop_BA_Experiments_Audiences_SearchLift"
                    primaryColumnHeader={audienceTypeColumnHeader}
                    primaryColumnHelpTopic="pop_BA_Experiments_AudienceListType"
                    title={searchLiftTitle}
                  />
                </Card>
                <PositivePerformingAudiencesCard
                  bottomData={searchBottomPerformerData}
                  topData={searchTopPerformerData}
                />
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        )}
        {useVisitLift && (
          <AccordionItem value={2} className="visit-lift">
            <AccordionHeader as="h5" size="large">
              <span className={styles.accordionHeader}>
                {visitLiftTitle}
              </span>
            </AccordionHeader>
            <AccordionPanel>
              <Stack tokens={layoutTokens}>
                <Card className={styles.card}>
                  <OverviewTable
                    items={visitData}
                    liftHelpTopic="pop_BA_Experiments_Audiences_VisitsLift"
                    primaryColumnHeader={audienceTypeColumnHeader}
                    primaryColumnHelpTopic="pop_BA_Experiments_AudienceListType"
                    title={visitLiftTitle}
                  />
                </Card>
                <PositivePerformingAudiencesCard
                  bottomData={visitBottomPerformerData}
                  topData={visitTopPerformerData}
                />
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        )}
        {useConversionLift && (
          <AccordionItem value={3} className="conversion-lift">
            <AccordionHeader as="h5" size="large">
              <span className={styles.accordionHeader}>
                {conversionLiftTitle}
              </span>
            </AccordionHeader>
            <AccordionPanel>
              <Stack tokens={layoutTokens}>
                <Card className={styles.card}>
                  <OverviewTable
                    items={conversionData}
                    liftHelpTopic="pop_BA_Experiments_Audiences_ConversionLift"
                    primaryColumnHeader={audienceTypeColumnHeader}
                    primaryColumnHelpTopic="pop_BA_Experiments_AudienceListType"
                    title={conversionLiftTitle}
                  />
                </Card>
                <PositivePerformingAudiencesCard
                  bottomData={conversionBottomPerformerData}
                  topData={conversionTopPerformerData}
                />
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        )}
      </Accordion>
    </>
  );
};

AudienceOverviewSection.propTypes = {
  conversionBottomPerformerData: PropTypes.arrayOf(PropTypes.shape({
    audienceId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    audienceTypeId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    rank: PropTypes.number.isRequired,
  })),
  conversionData: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    name: PropTypes.string.isRequired,
    confidence: PropTypes.string.isRequired,
    control: PropTypes.number.isRequired,
    lift: PropTypes.number.isRequired,
    treatment: PropTypes.number.isRequired,
  })).isRequired,
  conversionTopPerformerData: PropTypes.arrayOf(PropTypes.shape({
    audienceId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    audienceTypeId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    rank: PropTypes.number.isRequired,
  })).isRequired,
  measurementTypes: PropTypes.shape({
    useConversionLift: PropTypes.bool.isRequired,
    useSearchLift: PropTypes.bool.isRequired,
    useVisitLift: PropTypes.bool.isRequired,
  }).isRequired,
  searchBottomPerformerData: PropTypes.arrayOf(PropTypes.shape({
    audienceId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    audienceTypeId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    rank: PropTypes.number.isRequired,
  })),
  searchData: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    name: PropTypes.string.isRequired,
    confidence: PropTypes.string.isRequired,
    control: PropTypes.number.isRequired,
    lift: PropTypes.number.isRequired,
    treatment: PropTypes.number.isRequired,
  })).isRequired,
  searchTopPerformerData: PropTypes.arrayOf(PropTypes.shape({
    audienceId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    audienceTypeId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    rank: PropTypes.number.isRequired,
  })).isRequired,
  visitBottomPerformerData: PropTypes.arrayOf(PropTypes.shape({
    audienceId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    audienceTypeId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    rank: PropTypes.number.isRequired,
  })),
  visitData: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    name: PropTypes.string.isRequired,
    confidence: PropTypes.string.isRequired,
    control: PropTypes.number.isRequired,
    lift: PropTypes.number.isRequired,
    treatment: PropTypes.number.isRequired,
  })).isRequired,
  visitTopPerformerData: PropTypes.arrayOf(PropTypes.shape({
    audienceId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    audienceTypeId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    rank: PropTypes.number.isRequired,
  })).isRequired,
};

AudienceOverviewSection.defaultProps = {
  conversionBottomPerformerData: null,
  searchBottomPerformerData: null,
  visitBottomPerformerData: null,
};
